<template>
  <!-- L'abonnement d'un utilisateur, sous sa fiche (REDMINE-13522) : ce que le
       support consulte quand un client dit « j'ai payé et je n'ai pas le
       premium ». Les abonnements sont en lecture seule : le store fait foi, et
       seul le service Billing ecrit ces tables. Seul le premium offert depuis
       l'intranet (REDMINE-13728) s'ecrit ici. -->
  <v-card flat class="mt-6 user-billing">
    <v-card-title class="d-flex align-center">
      {{ $t('intranet.billing.title') }}
      <v-chip small class="ml-3" :color="premium ? 'success' : 'grey lighten-1'">
        {{ premium ? $t('intranet.billing.plan.premium') : $t('intranet.billing.plan.free') }}
      </v-chip>
    </v-card-title>
    <v-card-subtitle>{{ $t('intranet.billing.readOnly') }}</v-card-subtitle>

    <v-card-text>
      <div class="d-flex align-center mb-2">
        <h3>{{ $t('intranet.billing.grants.title') }}</h3>
        <v-spacer></v-spacer>
        <v-btn small depressed color="primary" @click="openOffer()">{{ $t('intranet.billing.grants.offer') }}</v-btn>
      </div>
      <p class="text-body-2">{{ $t('intranet.billing.grants.intro') }}</p>
      <v-data-table dense hide-default-footer :headers="grantHeaders" :items="grants" :loading="loading"
                    :no-data-text="$t('intranet.billing.grants.none')">
        <template v-slot:item.state="{ item }">
          <v-chip x-small :color="grantState(item) === 'active' ? 'success' : 'grey lighten-1'">
            {{ $t(`intranet.billing.grants.states.${grantState(item)}`) }}
          </v-chip>
        </template>
        <template v-slot:item.starts-at="{ item }">{{ date(item['starts-at']) }}</template>
        <template v-slot:item.expires-at="{ item }">{{ item['expires-at'] ? date(item['expires-at']) : $t('intranet.billing.grants.noEnd') }}</template>
        <template v-slot:item.granted-by="{ item }">{{ authorOf(item['granted-by-id']) }}</template>
        <template v-slot:item.revoked-at="{ item }">
          {{ item['revoked-at'] ? `${date(item['revoked-at'])} (${authorOf(item['revoked-by-id'])})` : '—' }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn v-if="!item['revoked-at'] && grantState(item) !== 'expired'" x-small text color="error" @click="askRevoke(item)">
            {{ $t('intranet.billing.grants.revoke') }}
          </v-btn>
        </template>
      </v-data-table>

      <h3 class="mt-6 mb-2">{{ $t('intranet.billing.subscriptions') }}</h3>
      <v-data-table dense hide-default-footer :headers="subscriptionHeaders" :items="subscriptions" :loading="loading"
                    :no-data-text="$t('intranet.billing.none')">
        <template v-slot:item.product="{ item }">{{ productOf(item) }}</template>
        <template v-slot:item.auto-renew="{ item }">{{ item['auto-renew'] ? $t('intranet.billing.yes') : $t('intranet.billing.no') }}</template>
        <template v-slot:item.expires-at="{ item }">{{ date(item['expires-at']) }}</template>
      </v-data-table>

      <h3 class="mt-6 mb-2">{{ $t('intranet.billing.transactions') }}</h3>
      <v-data-table dense hide-default-footer :headers="transactionHeaders" :items="transactions" :loading="loading">
        <template v-slot:item.purchased-at="{ item }">{{ date(item['purchased-at']) }}</template>
        <template v-slot:item.expires-at="{ item }">{{ date(item['expires-at']) }}</template>
        <template v-slot:item.price="{ item }">{{ price(item['price-micros'], item.currency) }}</template>
        <template v-slot:item.revoked-at="{ item }">{{ item['revoked-at'] ? `${date(item['revoked-at'])} (${item['revocation-reason'] || '?'})` : '—' }}</template>
      </v-data-table>

      <h3 class="mt-6 mb-2">{{ $t('intranet.billing.events') }}</h3>
      <v-data-table dense :headers="eventHeaders" :items="events" :loading="loading"
                    :footer-props="{ 'items-per-page-options': [10, 25, 50] }">
        <template v-slot:item.received-at="{ item }">{{ date(item['received-at']) }}</template>
        <template v-slot:item.error="{ item }">
          <span :class="item.error ? 'error--text' : ''">{{ item.error || '—' }}</span>
        </template>
      </v-data-table>
    </v-card-text>

    <AveineGrantOfferDialog v-model="offerDialog" :user-id="userId" @offered="load()" />

    <v-dialog v-model="revokeDialog" max-width="480">
      <v-card>
        <v-card-title>{{ $t('intranet.billing.grants.revokeTitle') }}</v-card-title>
        <v-card-text>{{ $t('intranet.billing.grants.revokeConfirmation') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="revokeDialog = false">{{ $t('intranet.referential.button.cancel') }}</v-btn>
          <v-btn text color="error" :loading="saving" @click="confirmRevoke()">{{ $t('intranet.billing.grants.revoke') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { errorHandler } from 'aveine-toolbox'
import { search, revokeGrant, grantState, profileName, formatPrice, formatDate } from './billing-api'
import AveineGrantOfferDialog from './AveineGrantOfferDialog.vue'

const ENTITLING = ['active', 'in_grace_period']

export default {
  name: 'AveineUserBilling',
  components: { AveineGrantOfferDialog },
  props: {
    userId: { type: String, required: true }
  },
  data () {
    return {
      loading: false,
      subscriptions: [],
      transactions: [],
      events: [],
      products: {},
      grants: [],
      grantUsers: {},
      offerDialog: false,
      revokeDialog: false,
      grantToRevoke: null,
      saving: false
    }
  },
  computed: {
    // Indicatif : le droit exact (marge, partage familial) est calcule par
    // Billing. Ici, un abonnement en cours dont l'echeance n'est pas passee,
    // ou un premium offert en cours.
    premium () {
      const now = new Date()
      return this.grants.some(g => this.grantState(g) === 'active') ||
        this.subscriptions.some(s => ENTITLING.includes(s.status) &&
          new Date(s['grace-expires-at'] || s['expires-at'] || 0) > now)
    },
    grantHeaders () {
      return [
        { text: this.$t('intranet.billing.grants.state'), value: 'state', sortable: false },
        { text: this.$t('intranet.billing.grants.startsAt'), value: 'starts-at', sortable: false },
        { text: this.$t('intranet.billing.grants.expiresAt'), value: 'expires-at', sortable: false },
        { text: this.$t('intranet.billing.grants.reason'), value: 'reason', sortable: false },
        { text: this.$t('intranet.billing.grants.grantedBy'), value: 'granted-by', sortable: false },
        { text: this.$t('intranet.billing.grants.revokedAt'), value: 'revoked-at', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
    },
    subscriptionHeaders () {
      return [
        { text: this.$t('intranet.billing.store'), value: 'store', sortable: false },
        { text: this.$t('intranet.billing.product'), value: 'product', sortable: false },
        { text: this.$t('intranet.billing.status'), value: 'status', sortable: false },
        { text: this.$t('intranet.billing.autoRenew'), value: 'auto-renew', sortable: false },
        { text: this.$t('intranet.billing.expiresAt'), value: 'expires-at', sortable: false },
        { text: this.$t('intranet.billing.environment'), value: 'environment', sortable: false },
        { text: this.$t('intranet.billing.ownership'), value: 'ownership', sortable: false }
      ]
    },
    transactionHeaders () {
      return [
        { text: this.$t('intranet.billing.purchasedAt'), value: 'purchased-at', sortable: false },
        { text: this.$t('intranet.billing.expiresAt'), value: 'expires-at', sortable: false },
        { text: this.$t('intranet.billing.price'), value: 'price', sortable: false },
        { text: this.$t('intranet.billing.revokedAt'), value: 'revoked-at', sortable: false },
        { text: 'ID', value: 'store-transaction-id', sortable: false }
      ]
    },
    eventHeaders () {
      return [
        { text: this.$t('intranet.billing.receivedAt'), value: 'received-at', sortable: false },
        { text: this.$t('intranet.billing.store'), value: 'store', sortable: false },
        { text: this.$t('intranet.billing.source'), value: 'source', sortable: false },
        { text: this.$t('intranet.billing.type'), value: 'type', sortable: false },
        { text: this.$t('intranet.billing.error'), value: 'error', sortable: false }
      ]
    }
  },
  methods: {
    date (value) { return formatDate(value, this.$i18n.locale) },
    price (micros, currency) { return formatPrice(micros, currency) },
    productOf (subscription) {
      const product = this.products[`billing_products:${subscription['product-id']}`] ||
        this.products[`billing-products:${subscription['product-id']}`]
      return product ? `${product['product-id']}${product['base-plan-id'] ? ` / ${product['base-plan-id']}` : ''}` : '—'
    },
    grantState (grant) { return grantState(grant) },
    /** Le nom de l'auteur, tire du profil inclus, sinon son identifiant. */
    authorOf (userId) { return profileName(this.grantUsers, userId) },
    openOffer () {
      this.offerDialog = true
    },
    askRevoke (grant) {
      this.grantToRevoke = grant
      this.revokeDialog = true
    },
    async confirmRevoke () {
      this.saving = true
      try {
        await revokeGrant(this.$auth, this.grantToRevoke.id)
        this.revokeDialog = false
        this.$toastr.s(this.$t('intranet.billing.grants.revoked'))
        await this.load()
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.saving = false
      }
    },
    async load () {
      this.loading = true
      try {
        const [grants, subscriptions, transactions, events] = await Promise.all([
          search(this.$auth, '/billing-grants', { 'filter[user.id]': this.userId, include: 'granted_by.profile,revoked_by.profile', sort: '-starts-at', 'page[size]': 50 }),
          search(this.$auth, '/billing-subscriptions', { 'filter[user.id]': this.userId, include: 'product', sort: '-expires-at', 'page[size]': 50 }),
          search(this.$auth, '/billing-transactions', { 'filter[subscription.user.id]': this.userId, sort: '-purchased-at', 'page[size]': 50 }),
          search(this.$auth, '/billing-store-events', { 'filter[user.id]': this.userId, sort: '-received-at', 'page[size]': 100 })
        ])
        this.grants = grants.items
        this.grantUsers = grants.included
        this.subscriptions = subscriptions.items
        this.products = subscriptions.included
        this.transactions = transactions.items
        this.events = events.items
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    }
  },
  created () {
    this.load()
  }
}
</script>
